import type { ReactNode } from 'react'

/**
 * The library corpus is drained page by page (LG-362), and two surfaces search
 * it: the browse table and the AI-mock link dialog. Both must say so when the
 * drain stops short, because a prefix of the library looks exactly like the
 * whole of it — a coach who searches and gets no hit concludes the problem
 * does not exist. One message, one treatment, both surfaces.
 */
export const INCOMPLETE_CORPUS_MESSAGE =
  '⚠ Incomplete list — the library did not finish loading, so search and filters cover only part of it.'

// The dashboard's warning-pill idiom (cf. the "⚠ link a question…" pill in
// schedule-card.tsx): dashed amber border over a tinted ground. The tint is
// not decoration — bare var(--cd-amber) text on var(--cd-shell) is about
// 3.1:1 in the light theme, under AA, which is the worst contrast to hand a
// warning. The pill weight also stops it reading as more faint metadata in a
// row of faint metadata.
const PILL_CLASS =
  'inline-block rounded-md border border-dashed border-(--cd-amber)/40 bg-(--cd-amber)/10 px-[7px] py-0.5 text-[10px] font-bold text-(--cd-amber)'

export interface CorpusStatusProps {
  /**
   * Accessible name for the live region. Named for the same reason
   * `shared/flash-note.tsx` names its own: an unnamed role="status" is
   * indistinguishable from every other status region — including the ones
   * dnd-kit mounts — in both queries and screen-reader announcements. Each
   * surface passes its own name so the two never collide.
   */
  label: string
  /** The drain stopped short: a failed page or the paging cap. */
  incomplete: boolean
  /** Pages are still arriving. Ignored while `incomplete` — see below. */
  streaming?: boolean
  /**
   * This region mounts together with its content — a dialog that opens onto an
   * already-truncated corpus — so aria-live's change-based announcement never
   * fires. Render as `role="alert"` instead, which is announced on insertion.
   */
  announceOnMount?: boolean
  className?: string
}

export function CorpusStatus(props: CorpusStatusProps): ReactNode {
  const {
    label,
    incomplete,
    streaming = false,
    announceOnMount = false,
    className,
  } = props

  // The truncation warning is the one that must never be swallowed, so it
  // wins whenever both are set (the hook's `isTruncated` only goes true once
  // streaming has stopped, but this component does not depend on that).
  const message = incomplete ? (
    <span className={PILL_CLASS}>{INCOMPLETE_CORPUS_MESSAGE}</span>
  ) : streaming ? (
    'loading the rest…'
  ) : null

  const regionClass = `text-[10px] text-(--cd-text-faint) ${className ?? ''}`

  // Written as two branches rather than one element with a computed `role`
  // because the a11y lint can only check a literal role, and an unchecked
  // `aria-label` on a live region is exactly the mistake worth catching.
  if (announceOnMount) {
    // Rendered only when it has something to say. `role="alert"` is assertive
    // and announces on insertion, so an empty one carrying only `aria-label`
    // can be read out as a bare "…loading status" every time the dialog opens.
    // The polite branch below has no such cost, which is why it mounts empty.
    if (message === null) return null
    return (
      <p role="alert" aria-label={label} className={regionClass}>
        {message}
      </p>
    )
  }

  // Mounted even while empty: aria-live regions only announce content
  // *changes*, so a region that appears together with its first message is
  // never read out (same reasoning as shared/flash-note.tsx). A caller that
  // cannot mount empty says so with `announceOnMount` above.
  return (
    <p role="status" aria-label={label} className={regionClass}>
      {message}
    </p>
  )
}
